<template>
  <v-container>
    <v-layout
      text-xs-center
      wrap
    >
      <!-- Dialog box to show Mentor information -->
      <v-dialog
        v-model="dialog"
        scrollable  
        persistent
        max-width="750px"
        transition="dialog-transition"
      >
        <v-card>
          <v-card-title>
            <span class="headline">Mentor Information for {{viewedItem.name}} </span>
          </v-card-title>

          <v-card-text>
            <v-container grid-list-md>
              <v-layout wrap>
                <v-flex xs6>
                  <v-text-field 
                    v-model="viewedItem.location" 
                    readonly
                    label="Location"></v-text-field>
                </v-flex>
                <v-flex xs6>
                  <v-text-field 
                    v-model="viewedItem.role" 
                    readonly
                    label="Location"></v-text-field>
                </v-flex>
                <v-flex xs12>
                  <v-text-field 
                    v-model="viewedItem.specialty" 
                    readonly
                    label="Specialty"></v-text-field>
                </v-flex>
                <v-flex xs12>
                  <v-text-field 
                    v-model="viewedItem.topics" 
                    readonly
                    label="Topics"></v-text-field>
                </v-flex>
                <v-flex xs12>
                  <v-textarea 
                    v-model="viewedItem.bio" 
                    readonly
                    label="Background"></v-textarea>
                </v-flex>
              </v-layout>
            </v-container>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" flat @click="close">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Dialogue to confirm relationship request --> 
      <v-dialog
        v-model="confirmDialog"
        persistent :overlay="false"
        max-width="500px"
        transition="dialog-transition"
      >
        <v-card>
          <v-card-title primary-title>
            <span class="headline">Please confirm</span>
          </v-card-title>
          <v-card-text>
            Are you sure you wish to request a mentoring relationship with {{selectedMentor.name}}
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn flat small color="secondary" @click="cancelRequest">Cancel</v-btn>
            <v-btn flat small color="primary" @click="createRelationship">Yes</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <!-- Visible Search Card -->
      <v-flex xs12 sm10 md10 lg10 xl8 offset-sm1 offset-md1 offset-lg1 offset-xl1>
        <v-card>
          <v-toolbar dark color="primary">
            <v-toolbar-title class="title">
              Search for a Mentor{{coachText}}
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-tooltip bottom>
              <v-btn 
                slot="activator"
                flat
                icon
                dark
                @click="showHelp = !showHelp">
                  <v-icon>help_outline</v-icon>
              </v-btn>
              <span v-if="!showHelp">Show help</span>
              <span v-else>Hide help</span>
            </v-tooltip>
          </v-toolbar>
          <v-card-title v-if="showHelp">
            <template>
              <v-layout row wrap>
                <v-flex xs12>
                  <p class="text-xs-left">You can search for a potential mentor by topic, specialty and role.  The facility will only return mentors in the directory who meet your criteria and who have current capacity to take on mentees.  If the directory is unable to suggest a suitable mentor you may need to change your search criteria or try at another time.</p>
                  <p class="text-xs-left">Once you have found a potential mentor you will be able to send them a message through the directory which will enable them to contact you for an initial conversation to determine whether you both wish to enter into a mentoring relationship.  Please note that this may take a little time and they will contact you using the details provided in your profile.</p>
                </v-flex>
              </v-layout>
            </template>
          </v-card-title>
          <v-card-text>
            <template>
              <v-layout row wrap>
                <v-flex xs5>
                  <h5 class="text-sm-left headline">Filter by -</h5>  
                </v-flex>
                <v-spacer></v-spacer>
                <v-flex xs5>
                  <v-btn small 
                    color="primary"
                    ripple
                    @click="clearFilters">
                    Clear Filters
                  </v-btn>
                </v-flex>
              </v-layout>
            </template>
            <template>
              <v-layout row wrap>
                <v-flex xs5>
                  <v-select
                        :items="configData.topics"
                        v-model="filters.topics"
                        label="Topics"
                        @change="filterTopics"
                  ></v-select>
                </v-flex>
                <v-spacer></v-spacer>
                <v-flex xs5>
                  <v-select
                        :items="configData.specialty"
                        v-model="filters.specialty"
                        label="Specialty"
                        @change="filterSpecialty"
                  ></v-select>
                </v-flex>
                <v-flex xs5>
                  <v-select
                        :items="configData.roles"
                        v-model="filters.role"
                        label="Role"
                        @change="filterRole"
                  ></v-select>
                </v-flex>
              </v-layout>
            </template>
          </v-card-text>
          <v-data-table
            :headers="getHeaders()"
            :items="mentors"
            :search="filters"
            :loading="mentorsLoading"
            :custom-filter="customFilter"
          >
            <template slot="items" slot-scope="props">
              <td class="text-xs-left">{{ props.item.name }}</td>
              <td class="text-xs-left">{{ props.item.topics.toString().replace(/,/g,", ") }}</td>
              <td class="text-xs-left">{{ props.item.specialty.toString().replace(/,/g,", ") }}</td>
              <td class="text-xs-left">{{ props.item.location }}</td>
              <td class="justify-center layout px-0">
                <v-tooltip left>
                  <v-icon
                    slot="activator"
                    class="mr-2 mt-2"
                    @click="viewItem(props.item)"
                  >
                    visibility
                  </v-icon>
                  <span>View mentor details</span>  
                </v-tooltip>
                <v-tooltip right>
                  <v-icon
                    slot="activator"
                    class="mr-2 mt-2"
                    @click="confirmRequest(props.item)"
                  >
                    person_add
                  </v-icon>
                  <span>Request relationship</span>  
                </v-tooltip>
              </td>
            </template>
            <v-alert slot="no-results" :value="true" color="error" icon="warning">
              Your search for "{{ search }}" found no results.
            </v-alert>
          </v-data-table>
        </v-card>

      </v-flex>

    </v-layout>
  </v-container>
</template>

<script>
  export default {
    data: () => ({
      search: '',
      filters: {
        topics: '',
        specialty: '',
        role: '',
        location: ''
      },
      dialog: false,
/*      headers: [
        { text: 'Mentor', value: 'name', width: '15%'},
        { text: 'Topics', value: 'topics', width: '25%'},
        { text: 'Specialty', value: 'specialty', width: '30%'},
        { text: 'Location', value: 'location', width: '30%'}
      ], */
      viewedIndex: -1,
      viewedItem: {},
      selectedMentor: {},
      defaultItem: {},
      relationshipRequest: {
        client: {
          name: null,
          email: null,
          telephone: null,
          location: null
        },
        mentor: {
          name: null,
          email: null,
          telephone: null,
          location: null
        },
        relationshipStatus: 'requested',
        rejectionReason: null,
        firstMeeting: null,
        requestDateTime: null,
        feedback: {
          mentor: null,
          client: null
        }
      },
      showHelp: false,
      confirmDialog: false
    }),
    created: function() {
      this.$store.dispatch("updateMentors")
    },
    computed: {
      mentors: function() {
        return this.$store.getters.getMentors
      },
      myProfile: function() {
        return this.$store.getters.getMyProfile
      },
      configData: function () {
        //return this.$store.getters.getForms
        return this.$store.getters.getConfig
      },
      mentorsLoading: function () {
        return this.$store.getters.getMentorLoading
      },
      includeCoach: function () {
        return this.$store.getters.getIncludeCoach
      },
      coachText: function () {
        if (this.includeCoach) {
          return '/Coach'
        } else {
          return ''
        }
      },
      mentorHeader: function () {
        if (this.includeCoach) {
          return 'Mentor/Coach'
        } else {
          return 'Mentor'
        }
      }
    },
    methods: {
      viewItem (item) {
        this.viewedIndex = this.mentors.indexOf(item)
        this.viewedItem = Object.assign({}, item)
        this.dialog = true
      },
      close () {
        this.dialog = false
        setTimeout(() => {
          this.viewedItem = Object.assign({}, this.defaultItem)
          this.viewedIndex = -1
        }, 300)
      },
      confirmRequest (item) {
        //grab the selected mentor into a new object
        this.selectedMentor = Object.assign({}, item)
        this.confirmDialog = true
      },
      cancelRequest () {
        this.confirmDialog = false
        //clear the selected mentor object
        setTimeout(() => {
          this.selectedMentor = Object.assign({}, this.defaultItem)
          //this.viewedIndex = -1
        }, 300)
      },
      createRelationship () {
        this.confirmDialog = false
        //grab the selected mentor into a new object
        //this.selectedMentor = Object.assign({}, item)
        //populate the relationship object
        //client details
        this.relationshipRequest.client.name = this.myProfile.name
        this.relationshipRequest.client.email = this.myProfile.email
        this.relationshipRequest.client.telephone = this.myProfile.telephone
        this.relationshipRequest.client.location = this.myProfile.location
        //mentor details
        this.relationshipRequest.mentor.name = this.selectedMentor.name
        this.relationshipRequest.mentor.email = this.selectedMentor.email
        this.relationshipRequest.mentor.telephone = this.selectedMentor.telephone
        this.relationshipRequest.mentor.location = this.selectedMentor.location
        //relationship details already set in the model
        var d = new Date()
        this.relationshipRequest.requestDateTime = d.toLocaleDateString() + ' ' + d.toLocaleTimeString();
        
        //call the action in the store to create the relationship
        this.$store.dispatch("createRelationship",this.relationshipRequest)

        //clear out the relationshipRequest 
        //client details
        this.relationshipRequest.client.name = null
        this.relationshipRequest.client.email = null
        this.relationshipRequest.client.telephone = null
        //mentor details
        this.relationshipRequest.mentor.name = null
        this.relationshipRequest.mentor.email = null
        this.relationshipRequest.mentor.telephone = null
        
        //clear the selected mentor object
        setTimeout(() => {
          this.selectedMentor = Object.assign({}, this.defaultItem)
          //this.viewedIndex = -1
        }, 300)

      },
      customFilter(items, filters, filter, headers) {
        // Init the filter class.
        const cf = new this.$MultiFilters(items, filters, filter, headers);

        // Use regular function(),
        // arrow functions does not allow context binding.
        // Register the global standard filter.
        cf.registerFilter('topics', function (searchWord, items) {
          if (searchWord.trim() === '') return items;
          return items.filter(item => {
            return item.topics.includes(searchWord);
          }, searchWord);
        });

        // Use regular function(),
        // arrow functions does not allow context binding.
        // Register "specialty" filter.
        cf.registerFilter('specialty', function (specialty, items) {
          // If the filter has not been applied yet
          // just return all available items.
          if (specialty.trim() === '') return items;
          // Compare each item specialty and just return the matching ones.
          return items.filter(item => {
            return item.specialty.includes(specialty);
          }, specialty);
        });
        
        // Use regular function(),
        // arrow functions does not allow context binding.
        // Register "specialty" filter.
        cf.registerFilter('role', function (role, items) {
          // If the filter has not been applied yet
          // just return all available items.
          if (role.trim() === '') return items;
          // Compare each item specialty and just return the matching ones.
          return items.filter(item => {
            return item.role == role;
          }, role);
        });
        
        // Use regular function(),
        // arrow functions does not allow context binding.
        // Register "location" filter.
        cf.registerFilter('location', function (location, items) {
          // If the filter has not been applied yet
          // just return all available items.
          if (location.trim() === '') return items;
          // Compare each item location and just return the matching ones.
          return items.filter(item => {
            return item.location.toLowerCase().includes(location.toLowerCase());
          }, location);
        });

        // Its time to run all created filters.
        // Will be executed in the order thay were defined.
        return cf.runFilters();
      },
      filterTopics(val) {
        // Handler when user input something at the "Topics" field.
        this.filters = this.$MultiFilters.updateFilters(this.filters, {topics: val});
      },
      filterSpecialty(val) {
        // Handler when user input something at the "Specialty" field.
        this.filters = this.$MultiFilters.updateFilters(this.filters, {specialty: val});
      },
      filterLocation(val) {
        //Handler when user input something at the "Location" field.
        this.filters = this.$MultiFilters.updateFilters(this.filters, {location: val});
      },
      filterRole(val) {
        //Handler when user input something at the "Role" field.
        this.filters = this.$MultiFilters.updateFilters(this.filters, {role: val});
      },
      clearFilters() {
        this.filters.topics = ''
        this.filters.specialty = ''
        this.filters.role = ''
        this.filters.location = ''
      },
      getHeaders() {
        // using a method to set the data table headers so I can change the header text dynamically
        return [
          { text: this.mentorHeader, value: 'name', width: '15%'},
          { text: 'Topics', value: 'topics', width: '25%'},
          { text: 'Specialty', value: 'specialty', width: '30%'},
          { text: 'Location', value: 'location', width: '30%'}
        ];
     }
    }
  }
</script>

<style>

</style>
